import React, { Component } from 'react';
import { Route, Switch, Router, Redirect } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ApolloProvider } from 'react-apollo';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';

import { ChatComponent } from './components/common';

import {
  Login,
  PostBox,
  RecommendHelp,
  Profile,
  Status,
  Home,
  Version,
  Error as ErrorPage,
  CompanyInfo,
  ConfidentialityPage,
  Payments,
  PaymentRedirect,
  MessageToClient,
  MessageToAgency,
  Thanks,
  Feedback,
  QualtricsFeedback,
  PaymentSuccess,
  CalculatorAwait,
  CustomerDetail,
  SAPIframe,
} from './pages/index.troy';
import MainLayout from './containers/MainLayout/MainLayout.troy';
import HomeContainer from './containers/HomeContainer/index.troy';

import client from './config/graphql';
import history from './config/history';
import store from './store';
import saga from './sagas';

import { connectTo } from './utils';
import { sagaMiddleware } from './middleware';
import {
  logout,
  setToken,
  handleError,
  getJWTOnOneTimeToken,
  setBusinessPartnerID,
  clearStore,
  setErrorStatus,
  clearURLHistory,
  setCustomHeaders,
  sapAuthentication,
} from './actions';

import * as routes from './constants/routes';
import { NOT_FOUND } from './constants/errorStatuses';

import { getUrlParams, getSearchUrl } from './utils/httpUtil';
import { isTokenValid } from './utils/jwt';
import ReduxToastr from './utils/toastr';
import { oneTimeTokenLoginRequest } from './utils/token';

// import image from './assets/img/noise.png';

const theme = createMuiTheme({
  palette: {
    primary: {
      main: '#1ac7b8',
    },
  },
  typography: {
    // fontFamily: ['"NeutrifStudio-Regular"', 'sans-serif'].join(','),
    useNextVariants: true,
  },
});

const headerRoutes = [
  { path: routes.POST_BOX, component: PostBox },
  { path: routes.STATUS, component: Status },
  { path: routes.RECOMMEND_HELP, component: RecommendHelp },
  { path: routes.PROFILE, component: Profile },
];

const AVAILABLE_HOMECONTAINER_ROUTES_AUTHENTICATED = [
  routes.ERROR,
  routes.COMPANY_INFO,
  routes.CONFIDENTIALITY,
  routes.MESSAGE_TO_CLIENT,
  routes.MESSAGE_TO_AGENCY,
  routes.THANKS,
  routes.FEEDBACK,
  routes.POST_BOX,
  routes.QUALTRICS_FEEDBACK,
  routes.STATUS,
  routes.RECOMMEND_HELP,
  routes.PROFILE,
  routes.HOME,
  routes.PAYMENTS,
  routes.PAYMENT_REDIRECT,
  routes.PAYMENT_SUCCESS,
  routes.CALCULATOR_AWAIT,
];

const AVAILABLE_HOMECONTAINER_ROUTES = [
  routes.ERROR,
  routes.COMPANY_INFO,
  routes.CONFIDENTIALITY,
  routes.FEEDBACK,
  routes.QUALTRICS_FEEDBACK,
  routes.THANKS,
];

class AppRouter extends Component {
  constructor(props) {
    super(props);
    const {
      token: tokenFromStorage,
      clearStore,
      setToken,
      clearURLHistory,
      setCustomHeaders,
      setBusinessPartnerID,
      sapAuthentication,
    } = this.props;

    const pathname = window.location.pathname;

    const {
      oneTimeToken: oneTimeTokenFromUrl,
      jwt: tokenFromUrl,
      ...urlParams
    } = getUrlParams();
    if (Object.keys(urlParams).length) {
      setCustomHeaders(urlParams);
    }

    this.state = {
      isSAPIframe: pathname && pathname === routes.SAP_IFRAME,
      isCustomerDetail: pathname && pathname === routes.CUSTOMER_DETAIL,
      oneTimeToken: oneTimeTokenFromUrl || null,
      isTokenLoading: false,
    };

    if (Object.keys(urlParams).length) {
      setCustomHeaders(urlParams);
    }

    if (this.state.oneTimeToken || tokenFromUrl) {
      if (tokenFromUrl && tokenFromUrl !== tokenFromStorage) {
        clearStore();
        setToken(tokenFromUrl);
        this.state = {
          ...this.state,
          isAuthentificationLoading: true,
        };
      } else if (this.state.oneTimeToken) {
        clearStore();
      }
      clearURLHistory(
        getSearchUrl({
          url: pathname,
          query: urlParams,
        })
      );
    }

    if (this.state.isSAPIframe) {
      try {
        const businessPartnerID = urlParams['businessPartnerID'];
        setBusinessPartnerID(businessPartnerID);
        sapAuthentication(businessPartnerID);
        console.log('businessPartnerID from url : ', businessPartnerID);
        this.state = {
          ...this.state,
          isAuthentificationLoading: true,
        };
      } catch (err) {}
    }
  }

  getTokenOnOneTime = () => {
    const { token, handleError, setToken } = this.props;
    const { oneTimeToken, isTokenLoading } = this.state;

    if (!isTokenLoading && oneTimeToken && !token) {
      this.setState({ isTokenLoading: true });
      oneTimeTokenLoginRequest(oneTimeToken)
        .then(setToken)
        .catch(handleError)
        .then(() => {
          this.setState({ oneTimeToken: null, isTokenLoading: false });
        });
    }
  };

  componentDidMount() {
    this.getTokenOnOneTime();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    console.log('businessPartnerID from props : ', nextProps.businessPartnerID);

    return prevState &&
      prevState.isAuthentificationLoading &&
      nextProps[
        window.location.pathname === routes.SAP_IFRAME ? 'authToken' : 'token'
      ]
      ? { isAuthentificationLoading: false }
      : null;
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.chatComponentHide !== prevProps.chatComponentHide ||
      this.props.chatBlur !== prevProps.chatBlur
    ) {
      const elements = document.getElementsByTagName('body');
      if (elements && elements.length) {
        const body = elements[0];

        if (this.props.chatComponentHide !== prevProps.chatComponentHide) {
          const disableChatClassName = 'embedded-chat_hide';
          if (this.props.chatComponentHide) {
            body.classList.add(disableChatClassName);
          } else {
            body.classList.remove(disableChatClassName);
          }
        }

        if (this.props.chatBlur !== prevProps.chatBlur) {
          const blurChatClassName = 'embedded-chat_blur';

          if (this.props.chatBlur) {
            body.classList.add(blurChatClassName);
          } else {
            body.classList.remove(blurChatClassName);
          }
        }
      }
    }

    this.getTokenOnOneTime();
  }

  render() {
    const { token, chatComponentHide, setErrorStatus, chatBlur } = this.props;
    const {
      isAuthentificationLoading,
      isTokenLoading,
      oneTimeToken,
    } = this.state;
    const isAuthenticated = isTokenValid(token);

    return isAuthentificationLoading ||
      isTokenLoading ||
      oneTimeToken ? null : (
      <MuiThemeProvider theme={theme}>
        <Router history={history}>
          {!isAuthenticated ? (
            <MainLayout isAuthenticated={isAuthenticated}>
              <Switch>
                <Route exact path="/" component={Login} />
                <Route path={routes.LOGIN} component={Login} />
                <Route path={routes.VERSION} component={Version} />
                <Route
                  render={(props) => {
                    return AVAILABLE_HOMECONTAINER_ROUTES.indexOf(
                      props.location.pathname
                    ) !== -1 ? (
                      <HomeContainer isAuthenticated={isAuthenticated}>
                        <Route path={routes.FEEDBACK} component={Feedback} />
                        <Route path={routes.THANKS} component={Thanks} />
                        <Route
                          path={routes.QUALTRICS_FEEDBACK}
                          component={QualtricsFeedback}
                        />
                        <Route
                          path={routes.COMPANY_INFO}
                          component={CompanyInfo}
                        />
                        <Route
                          path={routes.CONFIDENTIALITY}
                          component={ConfidentialityPage}
                        />
                        <Route path={routes.ERROR} component={ErrorPage} />
                      </HomeContainer>
                    ) : (
                      (() => {
                        setErrorStatus(NOT_FOUND);
                        return <Redirect to={routes.ERROR} />;
                      })()
                    );
                  }}
                />
              </Switch>
            </MainLayout>
          ) : (
            <Switch>
              <Route
                path={routes.CUSTOMER_DETAIL}
                component={(props) => (
                  <CustomerDetail
                    {...props}
                    isAuthenticated={isAuthenticated}
                  />
                )}
              />
              <Route
                path={routes.SAP_IFRAME}
                component={(props) => (
                  <SAPIframe {...props} isAuthenticated={isAuthenticated} />
                )}
              />
              <Route path={routes.LOGIN} component={Login} />
              <Route path={routes.VERSION} component={Version} />
              <MainLayout isAuthenticated={isAuthenticated}>
                <Route
                  render={(props) => {
                    return AVAILABLE_HOMECONTAINER_ROUTES_AUTHENTICATED.indexOf(
                      props.location.pathname
                    ) !== -1 ? (
                      <HomeContainer isAuthenticated={isAuthenticated}>
                        <Route exact path="/" component={Home} />

                        {headerRoutes.map((route, key) => (
                          <Route {...route} key={key} />
                        ))}
                        <Route
                          path={routes.MESSAGE_TO_CLIENT}
                          component={MessageToClient}
                        />
                        <Route
                          path={routes.MESSAGE_TO_AGENCY}
                          component={MessageToAgency}
                        />
                        <Route path={routes.THANKS} component={Thanks} />
                        <Route path={routes.FEEDBACK} component={Feedback} />
                        <Route path={routes.PAYMENTS} component={Payments} />
                        <Route
                          path={routes.QUALTRICS_FEEDBACK}
                          component={QualtricsFeedback}
                        />
                        <Route
                          path={routes.PAYMENT_SUCCESS}
                          component={PaymentSuccess}
                        />
                        <Route
                          path={routes.PAYMENT_REDIRECT}
                          component={PaymentRedirect}
                        />
                        <Route
                          path={routes.CALCULATOR_AWAIT}
                          component={CalculatorAwait}
                        />
                        <Route
                          path={routes.COMPANY_INFO}
                          component={CompanyInfo}
                        />
                        <Route
                          path={routes.CONFIDENTIALITY}
                          component={ConfidentialityPage}
                        />
                        <Route
                          exact
                          path={routes.ERROR}
                          component={(props) => (
                            <ErrorPage
                              {...props}
                              isAuthenticated={isAuthenticated}
                            />
                          )}
                        />
                      </HomeContainer>
                    ) : (
                      (() => {
                        setErrorStatus(NOT_FOUND);
                        return <Redirect to={routes.ERROR} />;
                      })()
                    );
                  }}
                />
              </MainLayout>
            </Switch>
          )}
        </Router>
        <ReduxToastr closeOnToastrClick={true} progressBar={true} />
        {(!this.state.isSAPIframe &&
          !this.state.isCustomerDetail &&
          !chatComponentHide && <ChatComponent />) ||
          null}
      </MuiThemeProvider>
    );
  }
}

const ConnectedAppRouter = connectTo(
  ({
    auth: { token },
    sap: { businessPartnerID, authToken },
    ui: {
      chat: { hide: chatComponentHide, blur: chatBlur },
    },
  }) => ({
    token,
    businessPartnerID,
    authToken,
    chatComponentHide,
    chatBlur,
  }),
  {
    logout,
    setToken,
    getJWTOnOneTimeToken,
    setBusinessPartnerID,
    clearStore,
    setErrorStatus,
    clearURLHistory,
    setCustomHeaders,
    sapAuthentication,
    handleError,
  },
  AppRouter
);

class App extends Component {
  render() {
    return (
      <Provider store={store}>
        <ApolloProvider client={client}>
          <ConnectedAppRouter />
        </ApolloProvider>
      </Provider>
    );
  }
}

export default App;
sagaMiddleware.run(saga);
